import React, { useState, useEffect, ReactElement, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import Notification from '@Common/Notification';
import './AddReviewTasks.scss';
import TutorialModal from './TutorialModal';
import Modal from '@Common/Modal/Modal';
import CreateTaskForm from './Tasks/CreateTaskForm';
import * as types from '@Constants/actionTypes';
import { Task } from '../Interfaces/Types';
import { AddReviewTasksProps, FormFilterResponse } from '../Interfaces/PropTypes';
import ImportOptions from './ImportOptions';

const defaultFormData = {
	id: 0,
	name: '',
	description: '', //#TODO Check where to map
	contingentId: '',
	phaseId: '',
	departmentId: '',
	communityId: '',
	jobTitleId: '',
	contingentName: '',
	contactName: '',
	contactEmail: '',
	additionalInfo: '',
	taskTagIds: [],
	isActive: true,
	isLocationTaskListActive: true,
	links: [],
	targetDaysOut: '',
	targetDays: '',
	targetWeeks: '',
	targetStaticDate: '',
	startDaysOut: '',
	startDays: '',
	startWeeks: '',
	startStaticDate: '',
	anticipatedEffort: '',
	steps: [],
	prereqTasks: [],
	showStaticDates: false, // only for ui
	createdByName: '', // Only display if Editing
	createdDate: '', // Only display if Editing
	modifiedByName: '', // Only display if Editing
	modifiedDate: '', // Only display if Editing
	isMilestone: false,
};

const AddReviewTasks = ({ setWizardSubStep, setImportType, projectKey, subscriptionId, nextStep }: AddReviewTasksProps): ReactElement => {
	const [showTutorial, setShowTutorial] = useState<boolean>(false);
	const [showCreateTask, setShowCreateTask] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [showImportOptions, setShowImportOptions] = useState<boolean>(false);
	// #TODO discuss this
	const [baselineId, setBaselineId] = useState<number>(0);
	const [currentFormState, setCurrentFormState] = useState({
		editTask: null as Task | null,
		unchangedTask: null as Task | null,
		newTask: null as Task | null,
		isFormValid: true,
		showDateError: false,
		isLoading: false,
		taskSearchResults: [],
		taskPrereqSearchResults: [],
		unsavedChanges: false,
		saveAndNewRetention: false,
		baselineId: baselineId,
	});

	const [formData, setFormData] = useState({
		id: 0,
		name: '',
		description: '', //#TODO Check where to map
		contingentId: '',
		phaseId: '',
		departmentId: '',
		communityId: '',
		jobTitleId: '',
		contingentName: '',
		baselineId: baselineId,
		contactName: '',
		contactEmail: '',
		additionalInfo: '',
		taskTagIds: [],
		isActive: true,
		isLocationTaskListActive: true,
		//  isLocationTaskDateAdjustable: (props.formFilters.isProjectWidgetActive) ? false : true,
		links: [],
		targetDaysOut: '',
		targetDays: '',
		targetWeeks: '',
		targetStaticDate: '',
		startDaysOut: '',
		startDays: '',
		startWeeks: '',
		startStaticDate: '',
		anticipatedEffort: '',
		steps: [],
		prereqTasks: [],
		showStaticDates: false, // only for ui
		createdByName: '', // Only display if Editing
		createdDate: '', // Only display if Editing
		modifiedByName: '', // Only display if Editing
		modifiedDate: '', // Only display if Editing
		isMilestone: false,
	});

	const [formFilters, setFormFilters] = useState<FormFilterResponse>({
		departments: [],
		subscriptionDepartments: [],
		baselineJobTitles: [],
		communities: [],
		phases: [],
		certifiedTaskId: 0,
		subscriptionFiles: [],
		taskTags: [],
		isLoaded: false,
		isStartDateActive: false,
		isTaskStepsActive: false,
		isTaskEffortActive: false,
		isTaskFeedbackActive: false,
		isTaskDependenciesActive: false,
		isProjectWidgetActive: false,
		latestBaselineId: 0,
		lowestCommunityId: 0,
	});

	const updateBaselineId = (newBaselineId: number) => {
		setBaselineId(newBaselineId);
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setFormFilters((prevState) => ({
			...prevState,
			latestBaselineId: newBaselineId,
		}));
	};

	const dispatch: Dispatch = useDispatch();
	//#TODO Fin right subId and BaselineId
	const getFormFilters = async () => dispatch(newProjectWizardActions.loadFormFilters(subscriptionId, baselineId));
	const saveBaselineTaskNewWizard = async (task: object) => dispatch(newProjectWizardActions.saveBaselineTask(task));

	const createTaskModal = useRef();

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		// Update currentFormState whenever baselineId changes
		setCurrentFormState({ ...currentFormState, baselineId: baselineId });
		setFormData({ ...formData, baselineId: baselineId });
	}, [baselineId]);

	useEffect(() => {
		// Sync currentFormState when formData changes, ensuring it is up-to-date
		setCurrentFormState((prevState) => ({
			...prevState,
			editTask: formData,
			unchangedTask: formData,
			newTask: formData,
		}));
	}, [formData]);

	const init = async () => {
		const suppressTutorial = localStorage.getItem('suppressNewProjectWizardTaskListTutorial');
		setShowTutorial(suppressTutorial === 'false' ? true : false);
	};

	const loadFormFilters = async () => {
		setIsLoading(true);
		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		try {
			const result = await getFormFilters();
			const payload = result.payload;
			if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_SUCCESS) {
				setFormFilters(payload);
				//We need a baseline before creating a task so we set it as the latest plus one.
				if (payload.latestBaselineId > 0) {
					const newBaselineId = baselineId === 0 ? payload.latestBaselineId + 1 : baselineId;

					if (baselineId === 0) {
						setBaselineId(newBaselineId);
						updateBaselineId(newBaselineId);
					}

					const updatedState = {
						...currentFormState,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					const updatedFormDataState = {
						...formData,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					setCurrentFormState(updatedState);
					setFormData(updatedFormDataState);
				}
			} else if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setCurrentFormState({
				...currentFormState,
				isLoading: false,
			});
			setIsLoading(false);
		}
	};

	const onFormChange = (name, value) => {
		setFormData((prevData) => {
			const updatedFormData = { ...prevData, [name]: value };

			// Update currentFormState directly within this callback to ensure it has the latest formData
			setCurrentFormState((prevState) => ({
				...prevState,
				editTask: updatedFormData,
				unchangedTask: updatedFormData,
				newTask: updatedFormData,
			}));

			return updatedFormData;
		});
	};

	const onSelectImportType = (templateType: string) => {
		setImportType(templateType);
		setWizardSubStep('c.1');
	};

	const validateForm = (task) => {
		const { name, jobTitleId, departmentId, communityId } = task;

		// if there are steps, make sure they all have a name
		for (let i = 0; i < task.steps?.length; i++) {
			if (task.steps[i].name == '') {
				return false;
			}
		}

		for (let i = 0; i < task.links?.length; i++) {
			const link = task.links[i];
			if (link.url.trim().length > 0 && link.name.trim().length === 0) {
				// if there are links, make sure they all have abbreviations
				// if there is white space clear it out so element validation is visible
				link.name = '';
				let links = task.links.slice();
				links.splice(i, 1, link);
				//#TODO FIX THIS
				onFormChange('links', links);
				return false;
			}
		}

		if (currentFormState.editTask.showStaticDates && !currentFormState.editTask.targetStaticDate) {
			return false;
		}

		if (
			currentFormState.editTask.showStaticDates === false &&
			(isNaN(currentFormState.editTask.targetWeeks) || currentFormState.editTask.targetWeeks === '')
		) {
			return false;
		}

		if (
			currentFormState.editTask.showStaticDates === false &&
			(isNaN(currentFormState.editTask.targetDays) || currentFormState.editTask.targetDays === '')
		) {
			return false;
		}

		if (name && name.trim().length > 0 && jobTitleId && departmentId && communityId) {
			return true;
		}

		return false;
	};

	const onSave = async (saveDataAndRetention) => {
		if (currentFormState.isLoading) {
			return;
		}

		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		let task = {
			...currentFormState.editTask,
			projectKey: projectKey, // Replace 'value' with the actual value
		};

		if (saveDataAndRetention) {
			setCurrentFormState({
				...currentFormState,
				saveAndNewRetention: !currentFormState.saveAndNewRetention,
			});
		}

		if (!validateForm(task)) {
			setCurrentFormState((prevState) => ({
				...prevState,
				isFormValid: false,
				isLoading: false,
			}));
			return;
		}

		if (currentFormState.editTask.showStaticDates) {
			// clear out any values in the other section that isn't visible so it does transport to api
			task.targetDaysOut = null;
			task.startDaysOut = null;
			task.targetDays = null;
			task.targetWeeks = null;
			task.startDays = null;
			task.startWeeks = null;
			task.isLocationTaskDateAdjustable = false;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive) {
				if (task.startStaticDate !== undefined || task.targetStaticDate !== undefined) {
					const start = new Date(task.startStaticDate);
					const target = new Date(task.targetStaticDate);

					// NOTE: you can start and finish something the same day
					if (target.getTime() < start.getTime()) {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: true,
							isFormValid: false,
							isLoading: false,
						}));
						return;
					} else {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: false,
							isFormValid: false,
							isLoading: false,
						}));
					}
				}
			}
		} else {
			// days and weeks
			const targetWeeks = task.targetWeeks === null ? 0 : parseInt(task.targetWeeks);
			const targetDays = task.targetDays === null ? 0 : parseInt(task.targetDays);
			const startWeeks = task.startWeeks === null ? 0 : parseInt(task.startWeeks || 0);
			const startDays = task.startDays === null ? 0 : parseInt(task.startDays || 0);

			// convert target/start dates to total days (values can be negative!)
			task.targetDaysOut = targetWeeks * 7 + targetDays;
			task.startDaysOut = startWeeks * 7 + startDays;

			task.startStaticDate = null;
			task.targetStaticDate = null;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive && task.startDaysOut > 0) {
				if (task.targetDaysOut > task.startDaysOut) {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: true,
						isFormValid: false,
						isLoading: false,
					}));
					return;
				} else {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: false,
						isFormValid: false,
						isLoading: false,
					}));
				}
			} else {
				task.startDaysOut = null;
			}
		}

		// if IsActive make sure isLocationTaskListActive is true
		if (task.isActive) {
			task.isLocationTaskListActive = true;
		}

		// Remove any blank links
		if (task.links && task.links.length) {
			task.links = task.links.filter((x) => x.name && x.name.trim().length && x.url && x.url.trim().length);
		}

		//transforms taskTagIds from string to array of strings
		if (task.taskTagIds && task.taskTagIds.length > 0) {
			task.taskTagIds = !Array.isArray(task.taskTagIds) ? task.taskTagIds.split(',') : task.taskTagIds.join().split(',');
		}

		setCurrentFormState((prevState) => ({
			...prevState,
			isFormValid: true,
			isLoading: true,
		}));

		try {
			const response = await saveBaselineTaskNewWizard(task);
			if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_SUCCESS) {
				Notification('success', 'Success', 'Baseline Task Created');
				const currentBaselineId = currentFormState.editTask.baselineId;
				if (saveDataAndRetention) {
					// retain these properties and keep the drawer open
					const retainProps = {
						isMilestone: currentFormState.editTask.isMilestone,
						jobTitleId: currentFormState.editTask.jobTitleId,
						departmentId: currentFormState.editTask.departmentId,
						communityId: currentFormState.editTask.communityId,
						baselineId: currentBaselineId,
						isActive: currentFormState.editTask?.isActive
					};

					const newTask = {
						...defaultFormData,
						...retainProps,
					};

					if (response.payload.baselineId && response.payload.baselineId !== 0) updateBaselineId(response.payload.baselineId);

					onDrawerComplete(currentBaselineId);

					setFormData(newTask);

					setCurrentFormState((prevState) => ({
						...prevState,
						editTask: newTask,
						isLoading: false,
						saveAndNewRetention: false,
						baselineId: baselineId,
					}));
				} else {
					//We assign this to a new baseline. wILL BE LOADED IN FORM FILTERS
					const newBaselineId = 0;
					onDrawerComplete();
					setCurrentFormState({
						...currentFormState,
						editTask: formData,
						unchangedTask: formData,
						newTask: formData,
						currentTask: formData,
					});
					updateBaselineId(newBaselineId);
					nextStep(currentBaselineId);
				}
			} else if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
				setCurrentFormState((prevState) => ({
					...prevState,
					isLoading: false,
				}));
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));
		} finally {
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));
		}
	};

	const resetFormData = (baselineId) => {
		setFormData({
			id: 0,
			name: '',
			description: '',
			contingentId: '',
			phaseId: '',
			departmentId: '',
			communityId: '',
			jobTitleId: '',
			contingentName: '',
			baselineId: baselineId,
			contactName: '',
			contactEmail: '',
			additionalInfo: '',
			taskTagIds: [],
			isActive: true,
			isLocationTaskListActive: true,
			links: [],
			targetDaysOut: '',
			targetDays: '',
			targetWeeks: '',
			targetStaticDate: '',
			startDaysOut: '',
			startDays: '',
			startWeeks: '',
			startStaticDate: '',
			anticipatedEffort: '',
			steps: [],
			prereqTasks: [],
			showStaticDates: false,
			createdByName: '',
			createdDate: '',
			modifiedByName: '',
			modifiedDate: '',
			isMilestone: false,
		});
	};

	//#TODO Implement modal61
	const openTaskModal = () => {
		setShowCreateTask(true);
		createTaskModal.current.show();
	};

	const onDrawerComplete = (baselineId = 0) => {
		if (baselineId === 0) {
			setShowCreateTask(false);
		}
		resetFormData();

		setCurrentFormState({
			...currentFormState,
			editTask: formData,
			unchangedTask: formData,
			newTask: formData,
			currentTask: formData,
		});
		//createTaskModal.current.hide()
	};

	const checkForChanges = () => {
		// compare unChangedTask to editTask if editing a task
		if (currentFormState.editTask.id != 0) {
			const haveTheSameValues = _.isEqual(currentFormState.unchangedTask, currentFormState.editTask);
			setCurrentFormState((prevState) => ({
				...prevState,
				unsavedChanges: !haveTheSameValues,
			}));
		}
	};

	const openTaskModalHandler = async () => {
		await loadFormFilters();
		setShowCreateTask(true);
	};

	return (
		<div className='add-review-tasks-content'>
			<div className='wizard-content-title'>How would you like to add tasks to your new project?</div>

			<div className='what-is-link'>
				<button type='button' className='btn btn-link' onClick={() => setShowTutorial(true)}>
					<i className='fa-regular fa-info-circle fa-lg'></i>
					&nbsp;&nbsp;Learn how Pacer organizes tasks.
				</button>
			</div>

			<div className='container'>
				<div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4'>
					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={() => setWizardSubStep('b.1')}>
							<div className='card-body'>
								<i className='fa-regular fa-table-list fa-2x'></i>
								<h5 className='card-title'>Copy from an Existing Project</h5>
								<p className='card-text'>Copy tasks from a preexisting project to your new project.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={() => setShowImportOptions(true)}>
							<div className='card-body'>
								<i className='fa-regular fa-file-import fa-2x'></i>
								<h5 className='card-title'>Import New Tasks From Excel</h5>
								<p className='card-text'>Use our import tool to create new tasks in bulk.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={openTaskModalHandler}>
							<div className='card-body'>
								<i className='fa-regular fa-circle-plus fa-2x'></i>
								<h5 className='card-title'>Create New Tasks in Pacer</h5>
								<p className='card-text'>You will create tasks in the next step.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center coming-soon-card'>
							<div className='card-body'>
								<i className='fa-regular fa-list-dropdown fa-2x'></i>
								<h5 className='card-title'>Select from a Template</h5>
								<p className='card-text'>Choose from our library of common Task Lists to get started</p>
							</div>

							<span className='badge pacer61-badge'>
								<i className='fa-regular fa-sparkles'></i>Coming Soon!
							</span>
						</div>
					</div>
				</div>

				{showImportOptions && (
					<ImportOptions onSelectImportType={onSelectImportType} />
				)}
			</div>

			{showTutorial && <TutorialModal setShowTutorial={setShowTutorial} />}
			{showCreateTask && (
				<Modal
					id='confirmExitSaveModal'
					title='Create New Baseline Task'
					body={
						<CreateTaskForm
							currentTask={formData}
							isFormValid={currentFormState.isFormValid}
							onFormChange={onFormChange}
							checkForChanges={checkForChanges}
							loadFormFilters={loadFormFilters}
							formFilters={formFilters}
							baselineId={baselineId}
							showDateError={currentFormState.showDateError}></CreateTaskForm>
					}
					primaryButtonText='Save Task and Create New'
					secondaryButtonText='Save Task and Close'
					onPrimaryClick={() => {
						onSave(true);
					}}
					onSecondaryClick={() => {
						onSave(false);
					}}
					onCloseClick={() => setShowCreateTask(false)}
					changeSecondaryColor={true}
				/>
			)}
		</div>
	);
};

export default AddReviewTasks;
